<template>
    <div v-if="showMedSync">
        <!-- MedSync Banner -->
        <MedSyncBanner
            ref="medsync-banner"
            :patient-id="patient.id"
            :rx-id="prescription.rxID"
            @view-details="handleViewMedSyncDetails"
            @add-to-medsync="handleAddToMedSync" />

        <!-- MedSync Modals -->
        <MedSyncDetailsModal
            ref="medsync-details-modal"
            :patient-id="patient.id" />

        <AddToMedSyncModal
            ref="add-to-medsync-modal"
            :patient-id="patient.id"
            :rx-id="prescription.rxID"
            @added="handleMedSyncAdded" />
    </div>
</template>

<script lang="ts">
// MedSync UI cluster, extracted from PrescriptionDetailsPage: the enrollment banner plus the
// details and add-to-MedSync modals, and the wiring between them (banner → open a modal;
// add-modal saved → refresh the banner). Read-only entity props in, no events out — the trio is
// self-wired via typed @Ref imperative calls, and the banner/modals own their own API calls
// (there is no MedSync service). This cluster has no watchers.
//
// Not moved: the MedSync short-fill block (medSyncShortFillData → prescription.dispensed) lives
// in the page's refill-batch load flow, not here; it is one of the three places dispensedQuantity
// is written and stays on the page untouched.
import { Component, Vue, Prop, Ref } from "vue-property-decorator";
import { Patient } from "@/models/Patient";
import { Prescription } from "@/models/Prescription";
import MedSyncBanner from "@/components/MedSync/MedSyncBanner.vue";
import MedSyncDetailsModal from "@/components/MedSync/MedSyncDetailsModal.vue";
import AddToMedSyncModal from "@/components/MedSync/AddToMedSyncModal.vue";

@Component({
    components: {
        MedSyncBanner,
        MedSyncDetailsModal,
        AddToMedSyncModal,
    },
})
export default class MedSyncSection extends Vue {
    @Prop({ required: true }) patient!: Patient;
    @Prop({ required: true }) prescription!: Prescription;

    @Ref('medsync-banner') medSyncBanner!: MedSyncBanner;
    @Ref('medsync-details-modal') medSyncDetailsModal!: MedSyncDetailsModal;
    @Ref('add-to-medsync-modal') addToMedSyncModal!: AddToMedSyncModal;

    // Rendered only once a patient is loaded (the banner/modals key off patient.id).
    get showMedSync(): boolean {
        return !!(this.patient && this.patient.id);
    }

    handleViewMedSyncDetails() {
        if (this.medSyncDetailsModal) {
            this.medSyncDetailsModal.show();
        }
    }

    handleAddToMedSync(data: { enrollmentId: number | null; rxId: string | null }) {
        if (this.addToMedSyncModal && data.rxId) {
            this.addToMedSyncModal.show(data.rxId);
        }
    }

    async handleMedSyncAdded(_data: { rxId: string; enrollmentId: number }) {
        // Refresh the banner to show the updated medication count.
        if (this.medSyncBanner) {
            await this.medSyncBanner.refresh();
        }
    }
}
</script>
